import React, { useState, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Flex, Box } from 'reflexbox'
import { useMutation } from '@apollo/react-hooks'
import * as queryString from 'query-string'
import * as authService from '../../services/auth'
import { authMutations } from '../../graphql/mutations'
import useAlertStack from 'ui-components/lib/hooks/useAlertStack'
import Spinner from 'ui-components/lib/components/Spinner'
import Button from 'ui-components/lib/components/Button'
import Typography from 'ui-components/lib/components/Typography'
import styled from 'styled-components'
import { LoginLogo } from '../../components'
import { useTheme } from '../../contexts/ThemeContext'
import { Shield, CheckCircle2, Lock, ExternalLink, AlertCircle } from 'lucide-react'

const Container = styled(Flex)`
  min-height: 100vh;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  
  /* Gradiente adaptativo ao tema */
  background: ${({ isDark }) => 
    isDark 
      ? 'linear-gradient(135deg, hsl(215, 35%, 12%) 0%, hsl(215, 40%, 18%) 50%, hsl(215, 35%, 12%) 100%)'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  };
  
  transition: background 0.3s ease;
  
  /* Efeito de partículas/ondas no fundo */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: ${({ isDark }) => 
      isDark 
        ? 'radial-gradient(circle, rgba(118, 131, 246, 0.1) 0%, transparent 70%)'
        : 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)'
    };
    animation: float 20s ease-in-out infinite;
    pointer-events: none;
  }
  
  @keyframes float {
    0%, 100% {
      transform: translate(0, 0) rotate(0deg);
    }
    33% {
      transform: translate(30px, -30px) rotate(120deg);
    }
    66% {
      transform: translate(-20px, 20px) rotate(240deg);
    }
  }
`

const Card = styled(Box)`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 20px;
  padding: 3rem;
  max-width: 520px;
  width: 100%;
  box-shadow: ${({ isDark }) => 
    isDark 
      ? '0 20px 60px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(118, 131, 246, 0.1)'
      : '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)'
  };
  position: relative;
  z-index: 1;
  animation: slideUp 0.4s ease-out;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ isDark }) => 
      isDark 
        ? '0 24px 70px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(118, 131, 246, 0.15)'
        : '0 24px 70px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.08)'
    };
  }
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
    border-radius: 16px;
  }
`

const LogoContainer = styled(Box)`
  text-align: center;
  margin-bottom: 2.5rem;
  animation: fadeIn 0.5s ease-out 0.1s both;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const HeaderSection = styled(Box)`
  text-align: center;
  margin-bottom: 2rem;
  animation: fadeIn 0.5s ease-out 0.2s both;
`

const IconWrapper = styled(Box)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${({ isDark }) => 
    isDark 
      ? 'linear-gradient(135deg, rgba(118, 131, 246, 0.2), rgba(118, 131, 246, 0.1))'
      : 'linear-gradient(135deg, rgba(118, 131, 246, 0.15), rgba(118, 131, 246, 0.08))'
  };
  margin-bottom: 1.5rem;
  border: 1px solid ${({ isDark }) => 
    isDark 
      ? 'rgba(118, 131, 246, 0.3)'
      : 'rgba(118, 131, 246, 0.2)'
  };
  
  svg {
    color: var(--purple-bnz);
    width: 32px;
    height: 32px;
  }
`

const InfoBox = styled(Box)`
  background: ${({ isDark }) => 
    isDark 
      ? 'hsl(215, 30%, 14%)'
      : '#f8f9fa'
  };
  border: 1px solid ${({ isDark }) => 
    isDark 
      ? 'hsl(215, 25%, 22%)'
      : '#e9ecef'
  };
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1.5rem 0;
  transition: all 0.3s ease;
  animation: fadeIn 0.5s ease-out 0.3s both;
  
  &:hover {
    border-color: ${({ isDark }) => 
      isDark 
        ? 'rgba(118, 131, 246, 0.4)'
        : 'rgba(118, 131, 246, 0.3)'
    };
    transform: translateX(4px);
  }
`

const PermissionItem = styled(Flex)`
  align-items: center;
  margin: 0.75rem 0;
  color: var(--text-color);
  
  svg {
    width: 18px;
    height: 18px;
    color: var(--success-color);
    margin-right: 0.75rem;
    flex-shrink: 0;
  }
`

const AppInfoBox = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: ${({ isDark }) => 
    isDark 
      ? 'hsl(215, 30%, 14%)'
      : '#f8f9fa'
  };
  border: 1px solid ${({ isDark }) => 
    isDark 
      ? 'hsl(215, 25%, 22%)'
      : '#e9ecef'
  };
  border-radius: 12px;
  margin: 1.5rem 0;
  transition: all 0.3s ease;
  animation: fadeIn 0.5s ease-out 0.4s both;
  
  svg {
    width: 24px;
    height: 24px;
    color: var(--purple-bnz);
    flex-shrink: 0;
  }
`

const ButtonContainer = styled(Flex)`
  margin-top: 2.5rem;
  gap: 1rem;
  animation: fadeIn 0.5s ease-out 0.5s both;
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`

const StyledButton = styled(Button)`
  flex: 1;
  min-height: 48px;
  font-weight: 600;
  text-transform: none;
  border-radius: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const PrimaryButton = styled(StyledButton)`
  background: var(--purple-bnz) !important;
  
  &:hover {
    background: var(--purple-dark) !important;
  }
`

const DenyButton = styled(StyledButton)`
  border-color: var(--border-color) !important;
  color: var(--text-color) !important;
  background: transparent !important;
  
  &:hover {
    background: ${({ isDark }) => 
      isDark 
        ? 'hsl(215, 30%, 18%)'
        : 'rgba(0, 0, 0, 0.04)'
    } !important;
    border-color: ${({ isDark }) => 
      isDark 
        ? 'hsl(215, 25%, 30%)'
        : 'var(--border-color)'
    } !important;
  }
  
  &:active {
    background: ${({ isDark }) => 
      isDark 
        ? 'hsl(215, 30%, 16%)'
        : 'rgba(0, 0, 0, 0.06)'
    } !important;
  }
  
  svg {
    color: var(--text-color);
  }
`

const FooterText = styled(Box)`
  margin-top: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  animation: fadeIn 0.5s ease-out 0.6s both;
  color: var(--text-secondary);
  font-size: 0.875rem;
  text-align: center;
  
  svg {
    width: 14px;
    height: 14px;
    opacity: 0.7;
    flex-shrink: 0;
  }
`

const SSOAuthorize = () => {
  const history = useHistory()
  const location = useLocation()
  const { showMessage } = useAlertStack()
  const { isDark } = useTheme()
  const [authorizing, setAuthorizing] = useState(false)
  const [generatedCode, setGeneratedCode] = useState(null)
  const [generatedState, setGeneratedState] = useState(null)

  // Pegar parâmetros da URL
  const queryParams = queryString.parse(location.search)
  const { client_id, redirect_uri, response_type, state } = queryParams

  const [authorizeSSO] = useMutation(authMutations.AUTHORIZE_SSO, {
    onCompleted: (data) => {
      const { code, state: returnedState, auto_authorized, requires_authorization } = data.authorizeSSO || {}
      
      if (code && redirect_uri) {
        // Construir URL de redirecionamento com código
        const redirectUrl = new URL(redirect_uri)
        redirectUrl.searchParams.set('code', code)
        if (returnedState) {
          redirectUrl.searchParams.set('state', returnedState)
        }
        
        // Se foi autorizado automaticamente, redirecionar imediatamente
        if (auto_authorized) {
          window.location.href = redirectUrl.toString()
          return
        }
        
        // Se não requer autorização manual, redirecionar
        if (requires_authorization === false) {
          window.location.href = redirectUrl.toString()
          return
        }
        
        // Se requer autorização, o usuário precisa clicar no botão "Autorizar"
        // O código já foi gerado, então armazenamos para usar quando clicar em autorizar
        setGeneratedCode(code)
        setGeneratedState(returnedState)
        setAuthorizing(false)
      } else {
        showMessage({
          title: 'Erro na autorização',
          message: 'Não foi possível gerar o código de autorização.',
          color: 'danger',
          position: 'bottom-left',
          time: 5000,
        })
      }
    },
    onError: (error) => {
      console.error('Erro ao autorizar SSO:', error)
      const errorMessage = error?.graphQLErrors?.[0]?.message || 
                          error?.message || 
                          'Erro ao processar autorização. Tente novamente.'
      
      showMessage({
        title: 'Erro na autorização',
        message: errorMessage,
        color: 'danger',
        position: 'bottom-left',
        time: 5000,
      })
      
      // Se o erro for de token inválido, redirecionar para login
      if (errorMessage.includes('Token') || errorMessage.includes('autenticação')) {
        setTimeout(() => {
          const returnUrl = encodeURIComponent(location.pathname + location.search)
          history.push(`/login?returnUrl=${returnUrl}`)
        }, 2000)
      }
    },
  })

  // Verificar se usuário está logado e tentar autorização automática
  useEffect(() => {
    const token = authService.getAccessToken()
    
    if (!token) {
      // Redirecionar para login com returnUrl para voltar aqui após login
      const returnUrl = encodeURIComponent(location.pathname + location.search)
      history.push(`/login?returnUrl=${returnUrl}`)
      return
    }

    // Validar parâmetros obrigatórios
    if (!client_id || !redirect_uri || !response_type) {
      showMessage({
        title: 'Parâmetros inválidos',
        message: 'client_id, redirect_uri e response_type são obrigatórios.',
        color: 'danger',
        position: 'bottom-left',
        time: 5000,
      })
      setTimeout(() => {
        history.push('/dashboard')
      }, 2000)
      return
    }

    // Tentar autorização automática (verifica se já autorizou anteriormente)
    setAuthorizing(true)
    authorizeSSO({
      variables: {
        clientId: client_id,
        redirectUri: redirect_uri,
        responseType: response_type || 'code',
        state: state || null,
      },
    })
  }, [client_id, redirect_uri, response_type, state, history, location, showMessage, authorizeSSO])

  const handleAuthorize = () => {
    if (!client_id || !redirect_uri || !response_type) {
      return
    }

    // Se já temos um código gerado, apenas redireciona
    if (generatedCode && redirect_uri) {
      const redirectUrl = new URL(redirect_uri)
      redirectUrl.searchParams.set('code', generatedCode)
      if (generatedState) {
        redirectUrl.searchParams.set('state', generatedState)
      }
      window.location.href = redirectUrl.toString()
      return
    }

    // Se não tem código, gera um novo
    setAuthorizing(true)
    authorizeSSO({
      variables: {
        clientId: client_id,
        redirectUri: redirect_uri,
        responseType: response_type || 'code',
        state: state || null,
      },
    })
  }

  const handleDeny = () => {
    // Redirecionar para a aplicação cliente com erro
    if (redirect_uri) {
      const redirectUrl = new URL(redirect_uri)
      redirectUrl.searchParams.set('error', 'access_denied')
      redirectUrl.searchParams.set('error_description', 'Usuário negou a autorização')
      if (state) {
        redirectUrl.searchParams.set('state', state)
      }
      window.location.href = redirectUrl.toString()
    } else {
      history.push('/dashboard')
    }
  }

  // Se não tem os parâmetros necessários, mostrar loading
  if (!client_id || !redirect_uri || !response_type) {
    return (
      <Container justifyContent="center" alignItems="center" isDark={isDark}>
        <Spinner color={isDark ? "white" : "white"} />
      </Container>
    )
  }

  return (
    <Container justifyContent="center" alignItems="center" isDark={isDark}>
      <Card isDark={isDark}>
        <LogoContainer>
          <LoginLogo />
        </LogoContainer>

        <HeaderSection>
          <IconWrapper isDark={isDark}>
            <Shield />
          </IconWrapper>
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom
            style={{ 
              fontWeight: 700,
              color: 'var(--text-color)',
              marginBottom: '0.5rem'
            }}
          >
            Autorizar Aplicação
          </Typography>
          <Typography 
            variant="body1" 
            align="center" 
            style={{ 
              color: 'var(--text-secondary)',
              fontSize: '1rem',
              lineHeight: '1.6'
            }}
          >
            Uma aplicação está solicitando acesso à sua conta
          </Typography>
        </HeaderSection>

        <InfoBox isDark={isDark}>
          <Typography 
            variant="body2" 
            fontWeight="bold" 
            gutterBottom
            style={{ 
              color: 'var(--text-color)',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Lock size={18} />
            A aplicação solicita acesso a:
          </Typography>
          <Box>
            <PermissionItem>
              <CheckCircle2 />
              <Typography variant="body2" style={{ color: 'var(--text-color)' }}>
                Seus dados básicos (nome, email)
              </Typography>
            </PermissionItem>
            <PermissionItem>
              <CheckCircle2 />
              <Typography variant="body2" style={{ color: 'var(--text-color)' }}>
                Autenticação na aplicação
              </Typography>
            </PermissionItem>
          </Box>
        </InfoBox>

        <AppInfoBox isDark={isDark}>
          <Shield size={24} />
          <Box flex={1}>
            <Typography 
              variant="body2" 
              fontWeight="bold"
              style={{ 
                color: 'var(--text-color)',
                marginBottom: '0.25rem'
              }}
            >
              Aplicação
            </Typography>
            <Typography 
              variant="body2" 
              style={{ 
                color: 'var(--text-secondary)',
                fontSize: '0.95rem'
              }}
            >
              RevisIa
            </Typography>
          </Box>
        </AppInfoBox>

        <ButtonContainer justifyContent="space-between">
          <DenyButton
            variant="outlined"
            color="secondary"
            onClick={handleDeny}
            disabled={authorizing}
            fullWidth
            isDark={isDark}
            style={{ 
              marginRight: '0.5rem'
            }}
          >
            {authorizing ? (
              <Spinner size="small" />
            ) : (
              <>
                <AlertCircle size={18} />
                <span>Negar</span>
              </>
            )}
          </DenyButton>
          <PrimaryButton
            variant="contained"
            color="primary"
            onClick={handleAuthorize}
            disabled={authorizing}
            fullWidth
            style={{ 
              marginLeft: '0.5rem'
            }}
          >
            {authorizing ? (
              <Spinner size="small" color="white" />
            ) : (
              <>
                <CheckCircle2 size={18} />
                <span>Autorizar</span>
              </>
            )}
          </PrimaryButton>
        </ButtonContainer>

        <FooterText>
          <ExternalLink size={14} />
          <span>Ao autorizar, você será redirecionado para a aplicação</span>
        </FooterText>
      </Card>
    </Container>
  )
}

export default SSOAuthorize

